// packages/web/src/lib/cascadeWindow.ts — P-A: THE MAPPER THE KIT ASKED FOR.
//
// `CapturedConfirmation` has rendered a three-step return ramp since the
// design system landed (decision d82176db: fresh -> default, closing ->
// warning, final -> critical — three steps on the existing status ramp, not
// a continuous fade). Core has computed the real return window since C2c.
// Nothing joined them: the Kit's own comment recorded the gap, the product
// site stayed empty rather than feed the component an invented phase, and
// the demo seeder called the primitive directly because no HTTP route
// reached it. GET /api/cascades/:id/window is that route; this is the
// mapping.
//
// WHAT `null` MEANS, and why it is not a fourth phase. `ExpiryPhase` is a
// ramp toward an expiry — every value on it says "you can still undo this,
// here is how urgent it is". Three of Core's four window states are not on
// that ramp at all: a SETTLED cascade is already closed, a CORRECTION_ONLY
// one never had (or has lost) a return path, and a DEPENDENT one has had
// its window closed EARLY by entanglement while the ceiling is still ahead.
// For all three, the honest answer is that no undo affordance belongs on
// the receipt — so this returns null, and a caller renders the receipt
// without one. Painting a closed window 'final' would show a critical undo
// button that is guaranteed to 409, which is a lie with a colour on it.
//
// THE RAMP READS THE CEILING AGAINST NOW — absolute time remaining, not a
// fraction elapsed. It has to: `CascadeWindow` carries the ceiling and
// nothing else about the window's shape (the cascade's created_at is not in
// it), so a percentage is not computable from what the route returns, and
// inventing one would mean a second request. Absolute is also what a person
// actually asks — "how long do I have", never "what fraction is left".
//
// WHY THE TYPE LIVES IN lib/api.ts AND THE MAPPER LIVES HERE — decision
// ce1c3046's boundary, enforced by ChassisBoundary.test.tsx. `lib/api.ts` is
// inside the PORTAL module graph (an external visitor holding a single-use
// link downloads it), and nothing in that graph may name '@vowric/ui': the
// barrel is flat, so one import pulls the Chassis and the material
// generators into a bundle that must never wear a chassis. `ExpiryPhase` is
// a @vowric/ui type, so this file names the barrel — and therefore this file
// must stay OUT of the portal graph. Keeping `CascadeWindow` in api.ts
// beside every other server shape is what keeps it out: api.ts declares the
// wire type, this module maps it, and only operator-side callers (Kit today,
// the S2 receipt next) import the mapper. Putting the type here instead
// would drag api.ts across the boundary, which is exactly how the lint
// caught this file the first time it was written.
import type { ExpiryPhase } from '@vowric/ui';
import type { CascadeWindow } from './api';

/**
 * The two ramp boundaries, in milliseconds of window REMAINING.
 *
 * Sized against the windows the Rexx manifest actually declares — P1D, P3D,
 * P7D, P30D. They are days, so the steps must be hours: a minutes-scale ramp
 * would leave every window 'fresh' until the last breath, and a days-scale
 * one would make the shortest declared window (P1D) born 'closing'. At 6h /
 * 1h, a one-day window is calm for eighteen hours, warns for five, and goes
 * critical for the last one; a thirty-day window spends its whole life
 * 'fresh' and still gets both warnings on the final day.
 *
 * Exported so the boundaries are ONE place, testable and greppable, rather
 * than two literals inside a branch.
 */
export const EXPIRY_CLOSING_MS = 6 * 60 * 60 * 1000;
export const EXPIRY_FINAL_MS = 60 * 60 * 1000;

/**
 * CascadeWindow -> ExpiryPhase. Returns null when no undo affordance
 * belongs on the receipt (see the header).
 *
 * The `state === 'open'` ceiling is re-checked against `now` rather than
 * trusted: the server derived that state at READ time, and a receipt can
 * sit on screen long after. A ceiling that has since passed reads as null —
 * the window is gone — instead of a 'final' button whose press the server
 * would refuse. Deriving on read is exactly what window.ts does server-side;
 * this is the same discipline one hop later.
 */
export function expiryPhaseFor(window: CascadeWindow, now: Date = new Date()): ExpiryPhase | null {
  if (window.state !== 'open') return null;

  const ceiling = Date.parse(window.windowCeilingAt);
  // An unparseable ceiling is not a reason to guess a phase. Fail to "no
  // undo shown", never to "undo, probably fine".
  if (Number.isNaN(ceiling)) return null;

  const remaining = ceiling - now.getTime();
  if (remaining <= 0) return null;
  if (remaining <= EXPIRY_FINAL_MS) return 'final';
  if (remaining <= EXPIRY_CLOSING_MS) return 'closing';
  return 'fresh';
}
